import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import '@fontsource/nunito-sans/400.css';
import '@fontsource/nunito-sans/500.css';
import '@fontsource/nunito-sans/600.css';
import '@fontsource/nunito-sans/700.css';
import App from './App';
import env from './env';
import pkg from '../package.json';
import reportWebVitals from './reportWebVitals';
import {
    isDynamicImportFailure,
    reloadOnceWithCacheBuster,
} from './utils/staleChunkRecovery';

Sentry.init({
    dsn: env.SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserApiErrorsIntegration(),
        Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true,
        }),
        Sentry.functionToStringIntegration(),
        Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
        }),
    ],
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    release: pkg.version,
    environment: env.ENV,
    tracesSampleRate: 1.0,
    enabled: env.ENV === 'production',
    ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
        'messaging/unsupported-browser',
        "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.",
        "This browser doesn't support the API's required to use the Firebase SDK",
        '3000ms timeout exceeded',
        '(messaging/unsupported-browser).',
        "Cannot read properties of null (reading 'postMessage')",
        "Cannot read properties of undefined (reading 'postMessage')",
    ],
});

window.addEventListener('vite:preloadError', (event) => {
    reloadOnceWithCacheBuster(event);
});

window.addEventListener('unhandledrejection', (event) => {
    if (isDynamicImportFailure(event.reason)) {
        reloadOnceWithCacheBuster(event);
    }
});

window.addEventListener('error', (event) => {
    if (isDynamicImportFailure(event.error ?? event.message)) {
        reloadOnceWithCacheBuster(event);
    }
});

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>,
);

reportWebVitals();
